import { Button, Divider, Select } from 'antd'
import { FC, KeyboardEvent, memo, ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import shallow from 'zustand/shallow'
import PerButton from '../../../components/custom_components/Button'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerIcon from '../../../components/custom_components/Icon'
import { highlightSearchTerm, Search, SearchType } from '../../../components/custom_components/Search/Search'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { useSubordinateWithSearch } from '../../../graphql/subordinates/useSubordinateWithSearch'
import { authenticatedAppRoutes } from '../../../routes/routes'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { EmploymentStatus, EmployeeSearch } from '../../../types'
import { SmartAvatar } from '../../avatar/SmartAvatar'
import { useEmployeeSearchStore } from './EmployeeSearchStore'
import { useEmployeeSearchTimer } from './useEmployeeSearchTimer'
import { updateLatestSearchResults } from './utils'

const { Option } = Select

export const EmployeeSearchComponent: FC = memo(() => {
  const { user } = useAuth()
  const { push } = useHistory()
  const { t } = useTranslation()

  const { loadSubordinates, subordinates, loading } = useSubordinateWithSearch()
  const [searchTerm, setSearchTerm, handleClear] = useEmployeeSearchTimer(loadSubordinates, '')

  const [search, setSearch, latestSearchResults, setLatestSearchResults, searchLabel] = useEmployeeSearchStore(
    (state) => [state.search, state.setSearch, state.latestSearchResults, state.setLatestSearchResults, state.searchLabel],
    shallow,
  )

  const searchOptions = useMemo(() => (subordinates.length ? [...subordinates].slice(0, 5) : undefined), [subordinates]) // Only first 5 employees are shown in the search dropdown

  const displayOptions = useMemo(() => {
    if (searchTerm.length > 2 && search.length > 2 && searchTerm.startsWith(search) && searchOptions) {
      return searchOptions
    } else if (!searchTerm.length && latestSearchResults) {
      return latestSearchResults
    }
    return []
  }, [searchTerm, search, searchOptions, latestSearchResults])

  const handleIconClicked = () => {
    if (searchTerm.length > 2) {
      setSearch(searchTerm)
      push(authenticatedAppRoutes.searchResults())
    }
  }

  const handleEnterPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm.length > 2) {
      setSearch(searchTerm)
      push(authenticatedAppRoutes.searchResults())
    }
  }

  const handleViewAllClick = useCallback(() => {
    setTimeout(() => {
      push(authenticatedAppRoutes.searchResults())
    }, 0)
  }, [push])

  const handleOptionClick = useCallback(
    (employee: EmployeeSearch) => {
      handleClear()
      updateLatestSearchResults(employee, latestSearchResults, setLatestSearchResults)
      // Second param checks if employee is active, if yes, redirect to public profile, if not, redirect to resigned profile
      push(authenticatedAppRoutes.profile(employee.id, employee.employmentStatus !== EmploymentStatus.Active))
    },
    [push, handleClear, latestSearchResults, setLatestSearchResults],
  )

  const handleDeleteOptionClick = useCallback(
    (event: any, employeeId: string) => {
      event.stopPropagation()
      const newArray = latestSearchResults.filter((obj) => obj.id !== employeeId)
      setLatestSearchResults(newArray)
    },
    [latestSearchResults, setLatestSearchResults],
  )

  const customDropdown = useCallback(
    (menu: ReactNode) => {
      return (
        <div className="dropdown" data-e2e-search-dropdown>
          {!!latestSearchResults.length && !searchTerm.length && (
            <>
              <div className="header">
                <PerIcon name="rescedule_fill" size={16} color="secondary" className="per-icon--mirror" />
                <PerText appearance="sidenote">{t('navigation.search.recentlyViewed')}</PerText>
              </div>
              <Divider className="divider" />
            </>
          )}
          {menu}
          {searchTerm.length > 2 && search.length > 2 && subordinates.length > 5 && (
            <div className="footer">
              <Divider className="divider" />
              <PerButton color="primary" text={t('navigation.search.viewAllResults')} onMouseDown={handleViewAllClick} />
            </div>
          )}
        </div>
      )
    },
    [subordinates, latestSearchResults, handleViewAllClick, t, search, searchTerm],
  )

  const renderOptions = useCallback(
    (employee: EmployeeSearch) => (
      <Option key={`employee-${employee.id}`} value={employee.id}>
        <div className="option" onClick={() => handleOptionClick(employee)}>
          <PerFlexRow align="middle" justify="space-between" className="fullWidth">
            <PerFlexRow align="middle">
              <SmartAvatar firstName={employee.firstName} lastName={employee.lastName} employeeId={employee.id} size="small" />
              <PerFlexRow direction="column">
                <PerText display="inline" ml="xxs" className="option-text">
                  {highlightSearchTerm(
                    computeFullname(user.nameDisplayPreference as NameDisplayPreference, employee.firstName, employee.lastName),
                    searchTerm,
                  )}
                </PerText>
                <PerText color="secondary" fontSize="xs" display="inline" ml="xxs">
                  {highlightSearchTerm(employee.companyName, searchTerm)}
                </PerText>
              </PerFlexRow>
            </PerFlexRow>
            {searchTerm.length === 0 && (
              <Button
                className="deleteOption per-btnCircle per-btnCircle--secondary per-btnCircle--small"
                icon="close"
                ghost
                onClick={(event) => handleDeleteOptionClick(event, employee.id)}
              />
            )}
          </PerFlexRow>
        </div>
      </Option>
    ),
    [handleOptionClick, searchTerm, user.nameDisplayPreference, handleDeleteOptionClick],
  )

  const notFoundContent =
    searchTerm.length > 2 && searchTerm === searchLabel && !loading && !subordinates.length
      ? t('navigation.search.noResults')
      : t('navigation.search.startTyping')

  return (
    <Search<EmployeeSearch>
      type={SearchType.expandable}
      value={undefined}
      placeholder={t('navigation.search.placeholder')}
      options={displayOptions}
      renderOptions={renderOptions}
      notFoundContent={notFoundContent}
      customDropdown={customDropdown}
      searchTerm={searchTerm}
      onSearch={setSearchTerm}
      onInputKeyDown={handleEnterPress}
      onOptionClick={handleOptionClick}
      loading={loading}
      onIconClicked={handleIconClicked}
    />
  )
})
